import { LocalStorageKeys } from './types'

const KEY_PREFIX = 'nutrisense:'
const SESSION_STORAGE_SUFFIX = ':session'

const SPLIT_STORAGE_FIELDS: Partial<Record<LocalStorageKeys, readonly string[]>> = {
  [LocalStorageKeys.OnboardingData]: ['insurance_plan', 'other_insurance_plan'],
  [LocalStorageKeys.PartnerFlowData]: [
    'insurancePlan',
    'otherInsurancePlan',
    'otherInsurancePlanType',
    'insurancePolicyHolder'
  ],
  [LocalStorageKeys.VideoCallData]: ['selected_insurance_plan', 'selected_insurance_plan_other']
}

const generateKey = (key: LocalStorageKeys) => `${KEY_PREFIX}${key}`
const generateSessionKey = (key: LocalStorageKeys) => `${generateKey(key)}${SESSION_STORAGE_SUFFIX}`

export const getItem = (key: LocalStorageKeys) => localStorage.getItem(generateKey(key))

export const setItem = (key: LocalStorageKeys, value: string) =>
  localStorage.setItem(generateKey(key), value)

export const removeItem = (key: LocalStorageKeys) => {
  localStorage.removeItem(generateKey(key))
  sessionStorage.removeItem(generateSessionKey(key))
}

type StoredObject = Record<string, unknown>

const isStoredObject = (value: unknown): value is StoredObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const readObjectFromStorage = (storage: Storage, storageKey: string): StoredObject | null => {
  try {
    const payload = storage.getItem(storageKey)

    if (!payload) return null

    const parsedPayload = JSON.parse(payload)
    return isStoredObject(parsedPayload) ? parsedPayload : null
  } catch {
    return null
  }
}

const writeObjectToStorage = (
  storage: Storage,
  storageKey: string,
  object: StoredObject | null
) => {
  if (!object || Object.keys(object).length === 0) {
    storage.removeItem(storageKey)
    return
  }

  storage.setItem(storageKey, JSON.stringify(object))
}

const getSplitStorageFields = (key: LocalStorageKeys): readonly string[] | null =>
  SPLIT_STORAGE_FIELDS[key] ?? null

const partitionObject = (object: StoredObject, phiFields: readonly string[]) => {
  const phiFieldSet = new Set(phiFields)
  const localObject: StoredObject = {}
  const sessionObject: StoredObject = {}

  Object.entries(object).forEach(([field, value]) => {
    if (phiFieldSet.has(field)) {
      sessionObject[field] = value
      return
    }

    localObject[field] = value
  })

  return { localObject, sessionObject }
}

const migrateLegacyPhiFields = (key: LocalStorageKeys, phiFields: readonly string[]) => {
  const localStorageKey = generateKey(key)
  const sessionStorageKey = generateSessionKey(key)
  const localObject = readObjectFromStorage(localStorage, localStorageKey) ?? {}
  const sessionObject = readObjectFromStorage(sessionStorage, sessionStorageKey) ?? {}

  const legacyPhiFields = phiFields.reduce<StoredObject>((legacyFields, field) => {
    if (field in localObject) {
      legacyFields[field] = localObject[field]
    }

    return legacyFields
  }, {})

  if (Object.keys(legacyPhiFields).length === 0) {
    return { localObject, sessionObject }
  }

  const sanitizedLocalObject = { ...localObject }
  phiFields.forEach((field) => {
    delete sanitizedLocalObject[field]
  })

  const nextSessionObject = { ...legacyPhiFields, ...sessionObject }

  writeObjectToStorage(localStorage, localStorageKey, sanitizedLocalObject)
  writeObjectToStorage(sessionStorage, sessionStorageKey, nextSessionObject)

  return {
    localObject: sanitizedLocalObject,
    sessionObject: nextSessionObject
  }
}

export const getObject = <T>(key: LocalStorageKeys): T | null => {
  const phiFields = getSplitStorageFields(key)

  if (!phiFields) {
    return readObjectFromStorage(localStorage, generateKey(key)) as T | null
  }

  const { localObject, sessionObject } = migrateLegacyPhiFields(key, phiFields)
  const mergedObject = { ...localObject, ...sessionObject }

  if (Object.keys(mergedObject).length === 0) {
    return null
  }

  return mergedObject as T
}

export const setObject = (key: LocalStorageKeys, object: { [key: string]: unknown }) => {
  const phiFields = getSplitStorageFields(key)

  if (!phiFields) {
    setItem(key, JSON.stringify(object))
    return
  }

  const { localObject, sessionObject } = partitionObject(object, phiFields)
  writeObjectToStorage(localStorage, generateKey(key), localObject)
  writeObjectToStorage(sessionStorage, generateSessionKey(key), sessionObject)
}
